import { supabase } from '../lib/supabase';
import type { Midia, MidiaFormData } from '../models/midia.model';

const BUCKET    = 'midias-videos';
const PAGE_SIZE = 1000;

export const midiasService = {
  /** Retorna todas as mídias de uma tela, ordenadas por `ordem` */
  async listarPorTela(telaId: string): Promise<Midia[]> {
    const result: Midia[] = [];
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from('midias')
        .select('*')
        .eq('tela_id', telaId)
        .order('ordem', { ascending: true })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw error;
      if (!data || data.length === 0) break;
      result.push(...(data as Midia[]));
      if (data.length < PAGE_SIZE) break;
      from += PAGE_SIZE;
    }
    return result;
  },

  /** Cria uma nova mídia */
  async criar(midia: MidiaFormData): Promise<Midia> {
    const { data, error } = await supabase
      .from('midias')
      .insert(midia as never)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Atualiza campos de uma mídia existente.
   * Esta atualização dispara o evento Realtime nos Players inscritos.
   */
  async atualizar(telaId: string, id: string, campos: Partial<MidiaFormData>): Promise<Midia> {
    const { data, error } = await supabase
      .from('midias')
      .update(campos as never)
      .eq('tela_id', telaId)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /** Remove o registro do banco E o arquivo físico do Storage */
  async deletar(telaId: string, id: string, storageUrl?: string | null): Promise<void> {
    if (storageUrl) {
      const path = storageUrl.split(`/${BUCKET}/`)[1];
      if (path) {
        // Falha no Storage aborta antes de apagar a linha: a mídia segue visível e dá para tentar de novo,
        // em vez de sumir do painel deixando o arquivo órfão no bucket.
        const { error: removeError } = await supabase.storage.from(BUCKET).remove([path]);
        if (removeError) throw removeError;
      }
    }
    const { error } = await supabase
      .from('midias')
      .delete()
      .eq('tela_id', telaId)
      .eq('id', id);
    if (error) throw error;
  },

  /**
   * Faz upload de um arquivo para o Storage e retorna a URL pública.
   * @param empresaId  ID do estabelecimento (pasta no bucket)
   * @param file       Arquivo a enviar
   * @param onProgress Callback com progresso 0-100
   */
  async upload(
    empresaId: string,
    file: File,
    onProgress: (pct: number) => void,
  ): Promise<string> {
    const ext  = file.name.split('.').pop() ?? 'bin';
    const nome = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const path = `${empresaId}/${nome}`;

    const contentType = file.type || `video/${ext}`;

    const { error } = await supabase.storage
      .from(BUCKET)
      .upload(path, file, {
        cacheControl:  '3600',
        upsert:        false,
        contentType,
        // @ts-expect-error — onUploadProgress é suportado mas não tipado no SDK
        onUploadProgress: (ev: { loaded: number; total: number }) => {
          onProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });

    if (error) {
      console.error('[midiasService.upload] Erro no storage:', {
        message: error.message,
        name:    (error as unknown as { name?: string }).name,
        path,
        contentType,
        size: file.size,
      });
      throw error;
    }

    const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(path);
    return urlData.publicUrl;
  },
};
